/* Shared, cached cross-client task aggregation.
 *
 * There is NO all-users task endpoint, so the Team screen (views/team.ts)
 * aggregates the per-client board — GET /clients/{slug}/tasks over the register
 * roster (GET /clients) — into one flat list, then rolls it up by
 * assignee_user_id (lib/team.ts). This module is the ONE shared fetch path for
 * that aggregation: one /clients read + one /tasks read per client, fetched in
 * parallel, memoized on a single promise so re-entering the Team screen is free.
 * It is a READ-ONLY projection of the one task ledger — never a second store.
 *
 * (My Work uses the purpose-built, session-scoped GET /me/tasks instead — see
 * lib/myWork.ts. Team needs EVERY member's work, which /me/tasks can't give, so
 * it falls back to this roster aggregation, exactly as the build brief directs.)
 */
import { request } from '../api/client.ts';
import type { RegisterClient } from '../views/register.ts';
import type { RosterTask } from './team.ts';

export interface RosterTasksResult {
  clients: RegisterClient[];
  tasks: RosterTask[];
}

interface RawTask {
  status?: string;
  assignee_user_id?: number | null;
  due_date?: string | null;
}

let cache: Promise<RosterTasksResult> | null = null;

async function fetchAll(): Promise<RosterTasksResult> {
  const clients = (await request<{ clients?: RegisterClient[] }>('/clients')).clients || [];
  const perClient = await Promise.all(
    clients.map(async (c) => {
      try {
        const r = await request<{ tasks?: RawTask[] }>(`/clients/${c.slug}/tasks`);
        return (r.tasks || []).map((t): RosterTask => ({
          status: String(t.status ?? ''),
          assignee_user_id: typeof t.assignee_user_id === 'number' ? t.assignee_user_id : null,
          due_date: t.due_date ?? null,
          client_slug: c.slug,
        }));
      } catch {
        return [] as RosterTask[]; // one unreachable client never blanks the rollup
      }
    }),
  );
  return { clients, tasks: perClient.flat() };
}

/** The shared cached aggregation. Fetched at most once; a rejection resets the
 * cache so a later visit can retry (mirrors main.ts's ensureLegacyPayload). */
export function fetchRosterTasks(): Promise<RosterTasksResult> {
  if (!cache) cache = fetchAll().catch((e) => { cache = null; throw e; });
  return cache;
}
